// ============================================================================
// EditRegistrantModal — fix typos in a registrant's contact info (Task #51).
// ============================================================================
// Opens from the Registrations panel's per-row "Edit" button. Updates
// uif_site.event_registration_detail. Status changes go through the
// existing status dropdown — this modal is only for name / email / phone /
// seats / notes.
// ============================================================================

import { useEffect, useState } from 'react';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { Modal } from './Modal';
import { api, type AdminRegistration } from '../api';

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  token: string;
  registrant: AdminRegistration | null;
}

export function EditRegistrantModal({ open, onClose, onSaved, token, registrant }: Props) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [seats, setSeats] = useState('1');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (registrant && open) {
      setFullName(registrant.full_name);
      setEmail(registrant.email);
      setPhone(registrant.phone ?? '');
      setSeats(String(registrant.attendee_count));
      setNotes(registrant.special_requests ?? '');
      setError('');
    }
  }, [registrant, open]);

  if (!registrant) return null;

  const save = async () => {
    setBusy(true); setError('');
    try {
      // Send only fields that actually changed, so we don't churn the row.
      const patch: any = {};
      if (fullName.trim() !== registrant.full_name) patch.full_name = fullName.trim();
      if (email.trim().toLowerCase() !== registrant.email) patch.email = email.trim();
      const phoneClean = phone.trim();
      if ((phoneClean || null) !== registrant.phone) patch.phone = phoneClean || null;
      const seatNum = Math.max(1, Math.min(10, parseInt(seats, 10) || 1));
      if (seatNum !== registrant.attendee_count) patch.attendee_count = seatNum;
      const notesClean = notes.trim();
      if ((notesClean || null) !== (registrant.special_requests ?? null)) patch.special_requests = notesClean || null;
      if (Object.keys(patch).length === 0) {
        setError('No changes to save.');
        setBusy(false);
        return;
      }
      await api.adminUpdateRegistrant(token, registrant.attendee_id, patch);
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Could not save changes.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Edit ${registrant.full_name}`} maxWidth="max-w-lg">
      <div className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>
        )}

        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Full name *</span>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            disabled={busy}
          />
        </label>

        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Email *</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            disabled={busy}
          />
        </label>

        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Phone (optional)</span>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(optional)"
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            disabled={busy}
          />
        </label>

        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Seats</span>
          <input
            type="number"
            min={1}
            max={10}
            value={seats}
            onChange={(e) => setSeats(e.target.value)}
            className="w-32 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            disabled={busy}
          />
          <p className="text-[11px] text-gray-500 mt-1">
            Changing seats does not re-validate capacity. Add guest names from the row after saving.
          </p>
        </label>

        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Anything we should know?</span>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Dietary needs, accessibility, etc."
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            disabled={busy}
          />
        </label>

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy || !fullName.trim() || !email.trim()}
            className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-1.5 text-sm hover:bg-uif-green-dark disabled:bg-gray-300"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Save changes
          </button>
        </div>
      </div>
    </Modal>
  );
}
